/**
 * Aggregates the gov-data signals about an arbitrary plate into a single
 * "used car report". Pure data layer — no DOM, no Firebase. The view
 * layer calls `analyzeUsedCar(plate)` and renders the returned structure.
 *
 * Sources (all CKAN, public, anonymous):
 *   - Vehicle technical record (incl. baalut / tokef_dt / mivchan_acharon_dt)
 *   - Ownership history (count + dates of ownership transfers)
 *   - Manufacturer recalls matched by tozeret_cd + year range + DEGEM
 *
 * NOT included (data not publicly available in Israel):
 *   - Per-vehicle accident history (CBS publishes aggregates only)
 *   - Mileage history (private to test stations)
 *   - Lien / repossession (רשם המשכונות — manual web check at ₪10)
 * The view layer surfaces these gaps as external-link buttons.
 */

import { findVehicle, fetchOwnershipHistory, fetchRecallsForVehicle } from '../../data/apis/ckan.js';
import { normalize as normalizePlate } from '../../shared/plate.js';
import { createLogger } from '../../core/logger.js';

const log = createLogger('usedCarCheck');

/**
 * @typedef {Object} OwnershipSummary
 * @property {number} count            How many recorded transfers (0 if unknown).
 * @property {string | null} firstDate ISO date of earliest transfer (null if unknown).
 * @property {string | null} lastDate  ISO date of latest transfer.
 * @property {number | null} avgYearsPerOwner  null when count<2.
 * @property {Array<{ date: string; baalut: string }>} timeline
 */

/**
 * @typedef {Object} LicenseSummary
 * @property {string | null} validUntil   tokef_dt as YYYY-MM-DD
 * @property {number | null} daysLeft     negative = expired
 * @property {string | null} lastTestDate mivchan_acharon_dt as YYYY-MM-DD
 */

/**
 * @typedef {{ kind: string; severity: 'high' | 'medium' | 'low'; text: string }} RedFlag
 *
 * @typedef {Object} UsedCarReport
 * @property {true} found
 * @property {string} plate
 * @property {any}   vehicle           gov record (tozeret_nm, kinuy_mishari, shnat_yitzur, baalut...)
 * @property {OwnershipSummary} ownership
 * @property {LicenseSummary}   license
 * @property {Array<{ id: string; desc: string; web: string }>} recalls
 * @property {RedFlag[]} redFlags
 *
 * @typedef {Object} NotFoundReport
 * @property {false} found
 * @property {string} plate
 *
 * @typedef {UsedCarReport | NotFoundReport} ReportResult
 */

/**
 * Run all lookups in parallel, build the structured report.
 * @param {string} rawPlate
 * @returns {Promise<ReportResult>}
 */
export async function analyzeUsedCar(rawPlate) {
  const plate = normalizePlate(rawPlate);
  if (!plate) return { found: false, plate: '' };

  const [vehicleRes, ownershipRes] = await Promise.allSettled([
    findVehicle(plate),
    fetchOwnershipHistory(plate),
  ]);

  const vehicle =
    vehicleRes.status === 'fulfilled' && vehicleRes.value.records.length > 0
      ? vehicleRes.value.records[0]
      : null;
  if (!vehicle) return { found: false, plate };

  // Recalls depend on the vehicle record (tozeret_cd) — run after we have it.
  /** @type {Array<{ id: string; desc: string; web: string }>} */
  let recalls = [];
  try {
    recalls = await fetchRecallsForVehicle(vehicle);
  } catch (err) {
    log.warn('recall lookup failed:', err);
  }

  const ownershipHistory = ownershipRes.status === 'fulfilled' ? ownershipRes.value : [];
  const ownership = summariseOwnership(ownershipHistory);
  const license = summariseLicense(vehicle);
  const redFlags = computeRedFlags({ vehicle, ownership, license, recalls });

  return {
    found: true,
    plate,
    vehicle,
    ownership,
    license,
    recalls,
    redFlags,
  };
}

// ====================================================================
// Internal: summary helpers
// ====================================================================

/**
 * @param {any[]} records  raw ownership-history records (sorted desc by date)
 * @returns {OwnershipSummary}
 */
function summariseOwnership(records) {
  if (!Array.isArray(records) || records.length === 0) {
    return { count: 0, firstDate: null, lastDate: null, avgYearsPerOwner: null, timeline: [] };
  }
  const timeline = records
    .map((r) => ({
      date: bestDate(r),
      baalut: String(r.baalut ?? r.BAALUT ?? '').trim(),
    }))
    .filter((t) => t.date)
    // sort ascending so first = earliest
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  if (timeline.length === 0) {
    return { count: 0, firstDate: null, lastDate: null, avgYearsPerOwner: null, timeline: [] };
  }
  const firstDate = timeline[0].date;
  const lastDate = timeline[timeline.length - 1].date;
  const yearsBetween = (new Date(lastDate).getTime() - new Date(firstDate).getTime())
                       / (1000 * 60 * 60 * 24 * 365.25);
  // count - 1 because N owners means N-1 changes spanning that range.
  const avgYearsPerOwner = timeline.length >= 2 ? yearsBetween / (timeline.length - 1) : null;
  return {
    count: timeline.length,
    firstDate,
    lastDate,
    avgYearsPerOwner,
    timeline,
  };
}

/** @param {any} vehicle @returns {LicenseSummary} */
function summariseLicense(vehicle) {
  const validUntilRaw = String(vehicle?.tokef_dt ?? '').trim();
  const lastTestRaw   = String(vehicle?.mivchan_acharon_dt ?? '').trim();
  const validUntil = parseDate(validUntilRaw);
  const lastTest   = parseDate(lastTestRaw);
  /** @type {number | null} */
  let daysLeft = null;
  if (validUntil) {
    const ms = new Date(validUntil).getTime() - Date.now();
    daysLeft = Math.floor(ms / (1000 * 60 * 60 * 24));
  }
  return {
    validUntil,
    daysLeft,
    lastTestDate: lastTest,
  };
}

/**
 * Compile the user-facing red flags from the aggregated data. Each flag
 * gets a severity so the view can sort + color them.
 *
 * @param {Object} args
 * @param {any} args.vehicle
 * @param {OwnershipSummary} args.ownership
 * @param {LicenseSummary} args.license
 * @param {Array<{ id: string }>} args.recalls
 * @returns {RedFlag[]}
 */
function computeRedFlags({ vehicle, ownership, license, recalls }) {
  /** @type {RedFlag[]} */
  const flags = [];

  // 1. Many owners in a short span.
  if (ownership.count >= 3 && ownership.avgYearsPerOwner !== null && ownership.avgYearsPerOwner < 2) {
    flags.push({
      kind: 'frequent-transfers',
      severity: 'high',
      text: `${ownership.count} בעלים ב־${formatYears(ownership.avgYearsPerOwner * (ownership.count - 1))} — ממוצע פחות משנתיים לבעלים. סימן אזהרה.`,
    });
  } else if (ownership.count >= 4) {
    flags.push({
      kind: 'many-owners',
      severity: 'medium',
      text: `${ownership.count} בעלים רשומים — מספר גבוה יחסית. בדוק סיבה להחלפות.`,
    });
  }

  // 2. Rental / Leasing / Taxi in the ownership timeline.
  const baalutCodes = new Set([
    ...ownership.timeline.map((t) => t.baalut.toUpperCase()),
    String(vehicle?.baalut ?? '').toUpperCase(),
  ]);
  if (baalutCodes.has('TAXI') || baalutCodes.has('מונית')) {
    flags.push({
      kind: 'was-taxi',
      severity: 'high',
      text: '🚕 הרכב היה רשום כמונית. בלאי מואץ צפוי במנוע, גיר ובלמים.',
    });
  }
  if (baalutCodes.has('RENT') || baalutCodes.has('השכרה')) {
    flags.push({
      kind: 'was-rental',
      severity: 'medium',
      text: '🚗 הרכב היה בהשכרה. דרישת בדיקת מוסך מומלצת.',
    });
  }
  if (baalutCodes.has('LEAS') || baalutCodes.has('ליסינג')) {
    flags.push({
      kind: 'was-leasing',
      severity: 'low',
      text: '🏢 הרכב היה בליסינג. בד"כ מתוחזק יותר טוב מהשכרה — אך בדוק טיפולים.',
    });
  }

  // 3. License/test status.
  if (license.daysLeft !== null) {
    if (license.daysLeft < 0) {
      flags.push({
        kind: 'license-expired',
        severity: 'high',
        text: `📅 רישיון הרכב פג לפני ${-license.daysLeft} ימים. לא ניתן לנסוע בו חוקית.`,
      });
    } else if (license.daysLeft < 30) {
      flags.push({
        kind: 'license-expiring',
        severity: 'medium',
        text: `📅 רישיון הרכב פג בעוד ${license.daysLeft} ימים. תצטרך לעבור טסט מיד אחרי הקנייה.`,
      });
    }
  }

  // 4. Open recalls.
  if (recalls.length > 0) {
    flags.push({
      kind: 'open-recalls',
      severity: 'high',
      text: `🔔 ${recalls.length} קריא${recalls.length === 1 ? 'ה' : 'ות'} יצרן פתוח${recalls.length === 1 ? 'ה' : 'ות'} לרכב זה. צריך להחתים תיקון.`,
    });
  }

  // 5. Production year vs first registration delta.
  const shnatYitzur = parseInt(String(vehicle?.shnat_yitzur ?? '0'), 10);
  const aliyaRaw = String(vehicle?.moed_aliya_lakvish ?? '').trim();
  const aliyaYear = aliyaRaw ? parseInt(aliyaRaw.slice(0, 4), 10) : 0;
  if (shnatYitzur && aliyaYear && aliyaYear - shnatYitzur >= 2) {
    flags.push({
      kind: 'late-registration',
      severity: 'medium',
      text: `🏭 שנת ייצור ${shnatYitzur} אך עלה לכביש רק ב־${aliyaYear} (פער ${aliyaYear - shnatYitzur} שנים). ייתכן "יבוא חדש" / רכב שעמד בחצר היבואן.`,
    });
  }

  // Sort by severity (high → medium → low).
  const order = /** @type {Record<RedFlag['severity'], number>} */ ({ high: 0, medium: 1, low: 2 });
  flags.sort((a, b) => order[a.severity] - order[b.severity]);
  return flags;
}

// ====================================================================
// Tiny date helpers (CKAN dates come in inconsistent formats)
// ====================================================================

/** Try common date field names; return the most likely date string. */
function bestDate(record) {
  const candidates = [
    record.baalut_dt, record.BAALUT_DT,
    record.kvuza_dt,
    record.t_aliya_lakvish, record.moed_aliya_lakvish,
  ];
  for (const c of candidates) {
    const d = parseDate(c);
    if (d) return d;
  }
  return '';
}

/** Normalize a CKAN date to ISO YYYY-MM-DD, or null if unparseable. */
function parseDate(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  // Already ISO?
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  // CKAN sometimes returns "2018-05-30T00:00:00"
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

/** "3.2" → "3 שנים", "0.5" → "6 חודשים" */
function formatYears(years) {
  if (!Number.isFinite(years) || years <= 0) return '';
  if (years >= 1) {
    const rounded = Math.round(years * 10) / 10;
    return `${rounded} שנים`;
  }
  const months = Math.round(years * 12);
  return `${months} חודשים`;
}
